import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plug, Plus, Save } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { usePermission } from '../../hooks/usePermission';
import { apiClient } from '../../lib/api-client';

type IntegrationRow = {
  id: string;
  label: string;
  description?: string;
  source: 'native' | 'custom_private';
  enabled: boolean;
  capabilities: Array<{ id: string; label: string; description?: string }>;
  serverRef?: {
    type?: 'seed' | 'registry' | 'inline';
    value?: string;
    transport?: 'http' | 'stdio';
    command?: string;
    args?: string[];
    env?: Record<string, string>;
    url?: string;
  };
};

const blankForm: IntegrationRow = {
  id: '',
  label: '',
  description: '',
  source: 'custom_private',
  enabled: true,
  capabilities: [],
  serverRef: { type: 'inline', transport: 'http', value: '', url: '' },
};

function normalizeCapabilities(input: unknown): Array<{ id: string; label: string; description?: string }> {
  if (!Array.isArray(input)) return [];
  return input
    .map((item: any) => {
      if (typeof item === 'string') {
        const id = item.trim();
        return id ? { id, label: id } : null;
      }
      const id = String(item?.id || '').trim();
      if (!id) return null;
      return {
        id,
        label: String(item?.label || id),
        description: item?.description ? String(item.description) : undefined,
      };
    })
    .filter(Boolean) as Array<{ id: string; label: string; description?: string }>;
}

function capabilitiesToText(capabilities: unknown): string {
  return normalizeCapabilities(capabilities).map((capability) => capability.id).join(', ');
}

function textToCapabilities(text: string): Array<{ id: string; label: string }> {
  return text.split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((id) => ({ id, label: id }));
}

function normalizeServerRef(ref: IntegrationRow['serverRef']): IntegrationRow['serverRef'] {
  if (!ref) return { type: 'inline', transport: 'http', value: '', url: '' };
  const value = ref.value || ref.url || ref.command || '';
  const transport = ref.transport || (ref.command ? 'stdio' : 'http');
  return {
    ...ref,
    type: ref.type || 'inline',
    transport,
    value,
    url: ref.url || (transport === 'http' ? value : undefined),
    command: ref.command || (transport === 'stdio' ? value : undefined),
  };
}

function serverRefSummary(row: IntegrationRow): string {
  const ref = normalizeServerRef(row.serverRef);
  if (!ref?.value && !ref?.url && !ref?.command) return 'No server ref configured';
  if (ref.type === 'seed') return `Seed MCP: ${ref.value}`;
  if (ref.transport === 'stdio') return `stdio: ${ref.command || ref.value}`;
  return ref.url || ref.value || 'No server ref configured';
}

export function Integrations() {
  const { isAllowed: canRead } = usePermission('integrations', 'read');
  const { isAllowed: canManage } = usePermission('integrations', 'manage');
  const [rows, setRows] = useState<IntegrationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<IntegrationRow>(blankForm);

  const loadRows = useCallback(async () => {
    if (!canRead) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.getAiIntegrations();
      setRows((Array.isArray(response.integrations) ? response.integrations : []).map((row: IntegrationRow) => ({
        ...row,
        capabilities: normalizeCapabilities(row.capabilities),
        serverRef: normalizeServerRef(row.serverRef),
      })));
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => `${row.id} ${row.label} ${row.description || ''}`.toLowerCase().includes(query));
  }, [rows, search]);

  const openEditor = (row?: IntegrationRow) => {
    setForm(row ? {
      ...row,
      capabilities: normalizeCapabilities(row.capabilities),
      serverRef: normalizeServerRef(row.serverRef),
    } : blankForm);
    setEditOpen(true);
  };

  const save = async () => {
    if (!form.id.trim()) return;
    setSaving(true);
    try {
      await apiClient.upsertAiIntegration(form.id.trim(), {
        ...form,
        capabilities: normalizeCapabilities(form.capabilities),
        serverRef: normalizeServerRef(form.serverRef),
      });
      setEditOpen(false);
      await loadRows();
    } catch (err: any) {
      setError(err?.message || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Plug}
        title="Integrations"
        description="Manage Cursor MCP connector catalog entries. Seed entries compile to portal MCP routes; inline and registry entries compile to custom Cursor MCP config."
        count={filtered.length}
        countLabel="integrations"
        actions={(
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search integrations..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              wrapperClassName="w-64"
            />
            {canManage ? (
              <Button onClick={() => openEditor()}>
                <Plus className="mr-2 h-4 w-4" />
                Add Integration
              </Button>
            ) : null}
          </div>
        )}
      />

      {!canRead ? (
        <Card><CardContent className="py-10 text-sm text-gray-500 dark:text-gray-400">No access.</CardContent></Card>
      ) : loading ? (
        <Card><CardContent className="py-10 text-sm text-gray-500 dark:text-gray-400">Loading integrations...</CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((row) => (
            <Card key={row.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{row.id}</p>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{row.label || row.id}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{row.description || 'No description'}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant={row.enabled ? 'success' : 'secondary'}>{row.enabled ? 'enabled' : 'disabled'}</Badge>
                    <Badge variant="outline">{row.source}</Badge>
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {serverRefSummary(row)}
                </div>
                {row.capabilities.length ? (
                  <div className="flex flex-wrap gap-1">
                    {row.capabilities.slice(0, 4).map((capability) => (
                      <Badge key={capability.id} variant="secondary">{capability.id}</Badge>
                    ))}
                    {row.capabilities.length > 4 ? <Badge variant="outline">+{row.capabilities.length - 4}</Badge> : null}
                  </div>
                ) : null}
                {canManage ? (
                  <Button variant="outline" className="w-full" onClick={() => openEditor(row)}>Edit</Button>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {error ? (
        <Card><CardContent className="py-3 text-sm text-red-600 dark:text-red-400">{error}</CardContent></Card>
      ) : null}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent maxWidth="4xl" className="max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit Integration' : 'Create Integration'}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-5 overflow-y-auto">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-100">
              Integrations become entries in generated `.cursor/mcp.json`. Use <strong>seed</strong> for built-in portal MCP routes, <strong>inline</strong> for a direct Cursor MCP server URL or command, and <strong>registry</strong> for a private registry reference.
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-200">Integration id</span>
                <Input
                  placeholder="github"
                  value={form.id}
                  onChange={(event) => setForm((prev) => ({ ...prev, id: event.target.value }))}
                  disabled={Boolean(form.id && rows.some((row) => row.id === form.id))}
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-200">Label</span>
                <Input
                  placeholder="GitHub"
                  value={form.label}
                  onChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="font-medium text-gray-700 dark:text-gray-200">Description</span>
                <Input
                  placeholder="What this MCP server gives Cursor agents access to"
                  value={form.description || ''}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-200">Source</span>
                <select
                  value={form.source}
                  onChange={(event) => setForm((prev) => ({ ...prev, source: event.target.value as 'native' | 'custom_private' }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="custom_private">custom_private</option>
                  <option value="native">native</option>
                </select>
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-200">Server ref type</span>
                <select
                  value={form.serverRef?.type || 'inline'}
                  onChange={(event) => setForm((prev) => ({
                    ...prev,
                    serverRef: normalizeServerRef({ ...(prev.serverRef || {}), type: event.target.value as 'seed' | 'registry' | 'inline' }),
                  }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="seed">seed portal MCP route</option>
                  <option value="inline">inline custom MCP server</option>
                  <option value="registry">registry/private MCP config</option>
                </select>
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-200">Transport</span>
                <select
                  value={form.serverRef?.transport || 'http'}
                  onChange={(event) => setForm((prev) => ({
                    ...prev,
                    serverRef: normalizeServerRef({ ...(prev.serverRef || {}), transport: event.target.value as 'http' | 'stdio' }),
                  }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="http">HTTP/SSE URL</option>
                  <option value="stdio">stdio command</option>
                </select>
              </label>
              <label className="flex items-end gap-2 pb-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                />
                enabled
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="font-medium text-gray-700 dark:text-gray-200">Capabilities</span>
                <Input
                  placeholder="repo_read, code_search, pr_create"
                  value={capabilitiesToText(form.capabilities)}
                  onChange={(event) => setForm((prev) => ({
                    ...prev,
                    capabilities: textToCapabilities(event.target.value),
                  }))}
                />
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {form.serverRef?.transport === 'stdio' ? 'Command' : form.serverRef?.type === 'seed' ? 'Seed path' : 'Server URL / registry ref'}
                </span>
                <Input
                  placeholder={form.serverRef?.transport === 'stdio' ? 'npx -y @modelcontextprotocol/server-github' : 'mcp/github or https://example.com/mcp'}
                  value={form.serverRef?.transport === 'stdio' ? (form.serverRef.command || form.serverRef.value || '') : (form.serverRef?.url || form.serverRef?.value || '')}
                  onChange={(event) => setForm((prev) => {
                    const current = normalizeServerRef(prev.serverRef);
                    const value = event.target.value;
                    return {
                      ...prev,
                      serverRef: normalizeServerRef({
                        ...current,
                        value,
                        ...(current?.transport === 'stdio' ? { command: value, url: undefined } : { url: value, command: undefined }),
                      }),
                    };
                  })}
                />
              </label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={() => void save()} disabled={saving || !form.id.trim()}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

